/**
 * SDX Runtime - shared utilities exposed via the browser import map.
 *
 * Every SDX component (React, Vue, Svelte, or any other framework)
 * imports runtime helpers through `@sdx/runtime`:
 *
 *   import { t, localizedUrl, path } from '@sdx/runtime';
 *
 * The `@sdx/runtime` import map entry resolves to the compiled output
 * of this file. Compilation is handled by sdx_vite's
 * LibraryAssetCompiler via the sdx/runtime library entry in
 * sdx.libraries.yml, so the artifact honors the active build output
 * mode (central or distributed) automatically.
 *
 * The module bridges framework components with Drupal's client-side
 * APIs without forcing each component to reference `window.Drupal`
 * directly, and degrades gracefully when Drupal is not available
 * (Storybook, unit tests, SSR).
 */

export { sharedSingleton, hasSharedSingleton } from './shared';

const SDX_EMPTY_MARKER = Symbol.for('sdx.runtime.empty');

/**
 * Returns FALSE when `mod` is the empty stub injected by SDX
 * for a missing optional alias; TRUE otherwise.
 *
 * Consumers feature-gate calls into optional sibling modules by
 * importing the alias as a namespace and passing it here. The
 * check works across module boundaries because the stub uses a
 * registered well-known symbol (`Symbol.for('sdx.runtime.empty')`)
 * that compares equal regardless of which bundle holds the
 * reference.
 *
 * @example
 * import * as Plans from '@sdx-module/sdx_plans/api';
 * import { isModuleAvailable } from '@sdx/runtime';
 *
 * if (isModuleAvailable(Plans)) {
 *   Plans.openPlan(planId);
 * }
 */
export const isModuleAvailable = (mod: unknown): boolean => {
  if (!mod || (typeof mod !== 'object' && typeof mod !== 'function')) {
    return true;
  }
  const ns = mod as {
    __sdxEmpty?: symbol;
    default?: { __sdxEmpty?: symbol } | null;
  };
  if (ns.__sdxEmpty === SDX_EMPTY_MARKER) return false;
  if (ns.default && ns.default.__sdxEmpty === SDX_EMPTY_MARKER) return false;
  return true;
};

type TranslateArgs = Record<string, string | number>;
type TranslateOptions = Record<string, unknown>;
type RouteParams = Record<string, string | number>;

/**
 * Translates a string using Drupal.t().
 *
 * Falls back to simple placeholder replacement when Drupal.t() is
 * unavailable (Storybook, unit tests, SSR). The guard reads the bare
 * `Drupal` global, not `window.Drupal`; a test stubbing only
 * `window.Drupal` takes the fallback path.
 *
 * `args` are substituted by Drupal.formatString(), which HTML-escapes
 * `@key` and wraps `%key` in a placeholder element - correct for
 * innerHTML sinks, double-escaped when the framework renders the
 * result as text (`A &amp; B`). Text-context callers translate without
 * args and substitute the values themselves.
 *
 * @param str - Source string in English.
 * @param args - Replacement tokens (@key, %key, !key).
 * @param options - Options passed to Drupal.t() (e.g., `{context: '...'}`).
 * @returns The translated string.
 */
export const t = (
  str: string,
  args?: TranslateArgs,
  options?: TranslateOptions,
): string => {
  if (typeof Drupal !== 'undefined' && typeof Drupal.t === 'function') {
    return Drupal.t(str, args, options);
  }
  if (!args) {
    return str;
  }
  let result = str;
  for (const [key, value] of Object.entries(args)) {
    result = result.split(key).join(String(value));
  }
  return result;
};

/**
 * Formats a plural string using Drupal.formatPlural().
 */
export const formatPlural = (
  count: number,
  singular: string,
  plural: string,
  args?: TranslateArgs,
): string => {
  if (typeof Drupal !== 'undefined' && typeof Drupal.formatPlural === 'function') {
    return Drupal.formatPlural(count, singular, plural, args);
  }
  const str = count === 1 ? singular : plural;
  return t(str, { '@count': String(count), ...args });
};

/**
 * Returns the current Drupal URL path prefix (e.g., '/de', '/en'),
 * or an empty string when no URL path prefix applies.
 *
 * Reads from drupalSettings.path.pathPrefix, which is Drupal's
 * authoritative signal for the active request's path prefix. An
 * empty string is the correct result under every language
 * negotiation method that does not put the language into the URL
 * path:
 *
 * - URL path negotiation, default language: pathPrefix is empty -> ''.
 * - URL path negotiation, non-default language: pathPrefix is e.g.
 *   'de/' -> '/de'.
 * - URL domain negotiation (de.example.com): pathPrefix is empty;
 *   the domain already carries the language so no prefix is needed.
 * - Session, user preference, or browser detection: pathPrefix is
 *   empty; the session cookie handles language selection.
 *
 * Returns an empty string when drupalSettings is unavailable (SSR,
 * Storybook, unit tests) because the default language is
 * site-specific and cannot be inferred without the server's help.
 */
export const getLanguagePrefix = (): string => {
  if (typeof drupalSettings === 'undefined' || !drupalSettings.path?.pathPrefix) {
    return '';
  }
  const prefix = drupalSettings.path.pathPrefix.replace(/\/$/, '');
  return prefix ? `/${prefix}` : '';
};

/**
 * Prepends the current URL path prefix to a path so language-aware
 * endpoints resolve to the right language variant.
 *
 * Under URL path negotiation this yields e.g.
 * '/de/jsonapi/node/article'. Under domain or session negotiation
 * the prefix is empty and the original path is returned unchanged;
 * the domain or session cookie carries the language signal and the
 * endpoint will resolve correctly without any path rewriting.
 *
 * @param urlPath - The path to localize (e.g., '/jsonapi/node/product').
 * @returns The same path with a URL path prefix prepended when one applies.
 */
export const localizedUrl = (urlPath: string): string =>
  getLanguagePrefix() + urlPath;

/**
 * Resolves a Drupal route name to a URL path.
 *
 * Works like Twig's path() function. Uses route patterns injected
 * into drupalSettings.sdx.route_patterns by the server. Modules can
 * extend the available routes server-side via
 * hook_sdx_route_patterns_alter().
 *
 * @param routeName - The route name (e.g., 'entity.node.canonical').
 * @param params - Route parameters (e.g., `{ node: 5 }`).
 * @returns The resolved URL path.
 */
export const path = (routeName: string, params?: RouteParams): string => {
  const sdxSettings = (typeof drupalSettings !== 'undefined'
    ? (drupalSettings.sdx as Record<string, unknown> | undefined)
    : undefined);
  const routePatterns = (sdxSettings?.route_patterns
    ?? {}) as Record<string, string>;
  const basePath = (typeof drupalSettings !== 'undefined'
    ? (drupalSettings.path as { basePath?: string } | undefined)?.basePath
    : undefined) ?? '/';

  let pattern = routePatterns[routeName];
  if (!pattern) {
    const message = `[SDX] Undeclared route: ${routeName}. Only declared routes are exposed to the client - add it to the component's .component.yml under sdx.routes, or to the sdx.settings route_patterns allowlist.`;
    if (sdxSettings?.dev) {
      throw new Error(message);
    }
    console.warn(message);
    return basePath;
  }

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      pattern = pattern.replace(`:${key}`, encodeURIComponent(String(value)));
    }
  }

  if (basePath !== '/') {
    pattern = basePath + pattern.substring(1);
  }
  return pattern;
};

/**
 * Generates an absolute URL for a Drupal route.
 *
 * Like path() but prepends the current origin.
 */
export const url = (routeName: string, params?: RouteParams): string =>
  window.location.origin + path(routeName, params);
